<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎵 Listening Note Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Target Note</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="note-input">Note to Listen For:</label>
                            <input
                                id="note-input"
                                v-model="targetNote"
                                type="text"
                                placeholder="e.g., C5, D#5, Bb5"
                                class="text-input"
                            />
                            <div class="note-hint text-caption text-medium-emphasis">
                                Change the target note for pitch detection
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Quick Note Select</h3>
                    <div class="property-group">
                        <div class="button-group">
                            <button
                                v-for="note in quickNotes"
                                :key="note"
                                @click="targetNote = note"
                                :class="{ active: targetNote === note }"
                                class="state-btn"
                            >
                                {{ note }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Clef Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Clef:</label>
                            <div class="button-group">
                                <button
                                    @click="clef = 'treble'"
                                    :class="{ active: clef === 'treble' }"
                                    class="state-btn"
                                >
                                    Treble
                                </button>
                                <button
                                    @click="clef = 'bass'"
                                    :class="{ active: clef === 'bass' }"
                                    class="state-btn"
                                >
                                    Bass
                                </button>
                                <button
                                    @click="clef = 'alto'"
                                    :class="{ active: clef === 'alto' }"
                                    class="state-btn"
                                >
                                    Alto
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Display Options</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showFingering"
                                />
                                Show fingering diagram
                            </label>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showNoteLabel"
                                />
                                Show note label under fingering
                            </label>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="autoStart"
                                />
                                Auto-start listening
                            </label>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="hideControls"
                                />
                                Hide controls (for wrapper mode)
                            </label>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Tuning Tolerance</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="tolerance-slider"
                                >Tolerance: {{ toleranceCents }} cents</label
                            >
                            <input
                                id="tolerance-slider"
                                type="range"
                                v-model.number="toleranceCents"
                                min="10"
                                max="100"
                                step="5"
                            />
                            <div class="note-hint text-caption text-medium-emphasis">
                                How far off pitch can still be "correct"
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Live Preview</h2>
                <div class="preview-box">
                    <ListeningNote
                        :target-note="targetNote"
                        :clef="clef"
                        :duration="duration"
                        :show-fingering="showFingering"
                        :show-note-label="showNoteLabel"
                        :tolerance-cents="toleranceCents"
                        :auto-start="autoStart"
                        :hide-controls="hideControls"
                        :note-width="noteWidth"
                        :note-height="noteHeight"
                        :fingering-width="fingeringWidth"
                        :fingering-height="fingeringHeight"
                        @status-change="handleStatusChange"
                    />
                </div>

                <!-- Emit Monitor -->
                <v-card class="emit-monitor bg-surface-light">
                    <v-card-title class="text-h6">
                        📡 Status Change Emissions
                        <v-chip
                            size="small"
                            :color="emitCount > 0 ? 'success' : 'default'"
                            class="ml-2"
                        >
                            {{ emitCount }} emits
                        </v-chip>
                    </v-card-title>
                    <v-card-text>
                        <div v-if="lastEmit" class="emit-display">
                            <div class="emit-row">
                                <strong>Status:</strong>
                                <span :class="'status-badge status-' + lastEmit.status">
                                    {{ lastEmit.status }}
                                </span>
                            </div>
                            <div v-if="lastEmit.detectedPitch" class="emit-row">
                                <strong>Detected:</strong>
                                {{ lastEmit.detectedPitch.note }}
                                ({{ lastEmit.detectedPitch.frequency.toFixed(2) }} Hz,
                                {{ lastEmit.detectedPitch.centsOff > 0 ? '+' : '' }}{{ lastEmit.detectedPitch.centsOff?.toFixed(1) }} cents)
                            </div>
                            <div v-if="lastEmit.noteError" class="emit-row">
                                <strong>Feedback:</strong>
                                {{ lastEmit.noteError.feedback }}
                            </div>
                            <div class="emit-row emit-timestamp">
                                <strong>Timestamp:</strong>
                                {{ lastEmit.timestamp }}
                            </div>
                        </div>
                        <div v-else class="no-emit-message">
                            Waiting for first emission...
                        </div>
                    </v-card-text>
                </v-card>

                <div class="size-controls bg-surface-light">
                    <h3 class="text-h6">Component Sizing</h3>

                    <div class="size-control-group">
                        <label for="note-width-slider"
                            >Note Width: {{ noteWidth }}px</label
                        >
                        <input
                            id="note-width-slider"
                            type="range"
                            v-model.number="noteWidth"
                            min="150"
                            max="400"
                            step="25"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="note-height-slider"
                            >Note Height: {{ noteHeight }}px</label
                        >
                        <input
                            id="note-height-slider"
                            type="range"
                            v-model.number="noteHeight"
                            min="80"
                            max="200"
                            step="20"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="fingering-width-slider"
                            >Fingering Width: {{ fingeringWidth }}px</label
                        >
                        <input
                            id="fingering-width-slider"
                            type="range"
                            v-model.number="fingeringWidth"
                            min="300"
                            max="700"
                            step="50"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="fingering-height-slider"
                            >Fingering Height: {{ fingeringHeight }}px</label
                        >
                        <input
                            id="fingering-height-slider"
                            type="range"
                            v-model.number="fingeringHeight"
                            min="120"
                            max="300"
                            step="20"
                        />
                    </div>
                </div>

                <v-card class="preview-info">
                    <v-card-title class="text-h6">Current Props</v-card-title>
                    <v-card-text>
                        <div class="prop-display">
                            <div><strong>targetNote:</strong> "{{ targetNote }}"</div>
                            <div><strong>clef:</strong> "{{ clef }}"</div>
                            <div><strong>duration:</strong> "{{ duration }}"</div>
                            <div><strong>showFingering:</strong> {{ showFingering }}</div>
                            <div><strong>showNoteLabel:</strong> {{ showNoteLabel }}</div>
                            <div><strong>toleranceCents:</strong> {{ toleranceCents }}</div>
                            <div><strong>autoStart:</strong> {{ autoStart }}</div>
                            <div><strong>hideControls:</strong> {{ hideControls }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Usage Instructions -->
        <v-card class="instructions-section">
            <v-card-title class="text-h5 text-secondary">
                How to Use
            </v-card-title>
            <v-card-text>
                <ol class="usage-list">
                    <li>
                        <strong>Select a target note</strong> using the input field or quick select buttons
                    </li>
                    <li>
                        <strong>Click "Start Listening"</strong> in the preview panel (you may need to grant microphone permissions)
                    </li>
                    <li>
                        <strong>Play the target note</strong> on your flute or instrument
                    </li>
                    <li>
                        <strong>Watch the visual feedback:</strong>
                        <ul>
                            <li><span class="status-badge status-listening">Gray border</span> = Listening (no pitch or wrong note)</li>
                            <li><span class="status-badge status-correct">Green border</span> = Correct note, in tune!</li>
                            <li><span class="status-badge status-close">Yellow border</span> = Correct note, but adjust tuning</li>
                            <li><span class="status-badge status-wrong">Red border</span> = Wrong note detected</li>
                        </ul>
                    </li>
                    <li>
                        The component displays the <strong>detected pitch</strong> and how many <strong>cents</strong> you're off
                    </li>
                </ol>
            </v-card-text>
        </v-card>

        <!-- Code Output -->
        <v-card class="code-section">
            <v-card-title>
                <div class="code-header">
                    <h2 class="text-h6">Component Code</h2>
                    <v-btn @click="copyCode" size="small" variant="outlined">
                        📋 Copy
                    </v-btn>
                </div>
            </v-card-title>
            <v-card-text>
                <pre><code>{{ componentCode }}</code></pre>
            </v-card-text>
        </v-card>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Props</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Prop</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>targetNote</code></td>
                            <td><code>string</code></td>
                            <td>required</td>
                            <td>The note to listen for (e.g., "C5", "D#5", "Bb4")</td>
                        </tr>
                        <tr>
                            <td><code>clef</code></td>
                            <td><code>string</code></td>
                            <td>"treble"</td>
                            <td>Musical clef for display (treble, bass, alto, tenor)</td>
                        </tr>
                        <tr>
                            <td><code>duration</code></td>
                            <td><code>string</code></td>
                            <td>"quarter"</td>
                            <td>Note duration (whole, half, quarter, eighth, sixteenth)</td>
                        </tr>
                        <tr>
                            <td><code>noteWidth</code></td>
                            <td><code>number</code></td>
                            <td>200</td>
                            <td>Width of the music note display in pixels</td>
                        </tr>
                        <tr>
                            <td><code>noteHeight</code></td>
                            <td><code>number</code></td>
                            <td>120</td>
                            <td>Height of the music note display in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingeringWidth</code></td>
                            <td><code>number</code></td>
                            <td>500</td>
                            <td>Width of the fingering diagram in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingeringHeight</code></td>
                            <td><code>number</code></td>
                            <td>180</td>
                            <td>Height of the fingering diagram in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingering</code></td>
                            <td><code>FingeringState</code></td>
                            <td>auto</td>
                            <td>Custom fingering state, or auto-detected from note</td>
                        </tr>
                        <tr>
                            <td><code>showFingering</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Whether to display the fingering diagram</td>
                        </tr>
                        <tr>
                            <td><code>showNoteLabel</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Show note label under fingering diagram</td>
                        </tr>
                        <tr>
                            <td><code>toleranceCents</code></td>
                            <td><code>number</code></td>
                            <td>50</td>
                            <td>How many cents off pitch is still considered "correct"</td>
                        </tr>
                        <tr>
                            <td><code>autoStart</code></td>
                            <td><code>boolean</code></td>
                            <td>false</td>
                            <td>Automatically start listening when component mounts</td>
                        </tr>
                        <tr>
                            <td><code>hideControls</code></td>
                            <td><code>boolean</code></td>
                            <td>false</td>
                            <td>Hide the start/stop listening button (useful when wrapping the component)</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Events</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Event</th>
                            <th>Payload</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>status-change</code></td>
                            <td><code>(status: string, detectedPitch: PitchDetectionResult | null, noteError: NoteError | null)</code></td>
                            <td>Emitted when status changes (idle, listening, correct, close, wrong). Includes detected pitch data and feedback analysis.</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Features</h3>
                <ul class="api-list">
                    <li><strong>Real-time pitch detection:</strong> Uses Web Audio API and pitch detection library</li>
                    <li><strong>Visual feedback:</strong> Color-coded borders indicate listening status and accuracy</li>
                    <li><strong>Tuning display:</strong> Shows detected note and cents deviation</li>
                    <li><strong>Status change events:</strong> Emits status updates for wrapper components</li>
                    <li><strong>Microphone access:</strong> Automatically requests and manages microphone permissions</li>
                    <li><strong>Auto-cleanup:</strong> Properly stops audio streams when unmounted</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Status Colors</h3>
                <ul class="api-list">
                    <li><strong>Gray:</strong> Listening, no pitch detected or wrong note</li>
                    <li><strong>Green:</strong> Correct note detected, in tune (within 10 cents)</li>
                    <li><strong>Yellow:</strong> Correct note detected, but needs tuning adjustment</li>
                    <li><strong>Red:</strong> Wrong note detected</li>
                </ul>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import ListeningNote from "../components/ListeningNote.vue";
import type { PitchDetectionResult } from "../pitch";
import type { NoteError } from "../pitch/feedbackAnalysis";

const targetNote = ref("C5");
const clef = ref("treble");
const duration = ref("quarter");
const showFingering = ref(true);
const showNoteLabel = ref(true);
const toleranceCents = ref(50);
const autoStart = ref(false);
const hideControls = ref(false);
const noteWidth = ref(200);
const noteHeight = ref(120);
const fingeringWidth = ref(500);
const fingeringHeight = ref(180);

const quickNotes = ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5", "A5", "B5", "C6"];

// Emit tracking
const emitCount = ref(0);
const lastEmit = ref<{
    status: string;
    detectedPitch: PitchDetectionResult | null;
    noteError: NoteError | null;
    timestamp: string;
} | null>(null);

function handleStatusChange(
    status: string,
    detectedPitch: PitchDetectionResult | null,
    noteError: NoteError | null
) {
    emitCount.value++;
    lastEmit.value = {
        status,
        detectedPitch,
        noteError,
        timestamp: new Date().toLocaleTimeString(),
    };
}

const componentCode = computed(() => {
    const props: string[] = [];

    props.push(`  target-note="${targetNote.value}"`);

    if (clef.value !== "treble") {
        props.push(`  clef="${clef.value}"`);
    }

    if (duration.value !== "quarter") {
        props.push(`  duration="${duration.value}"`);
    }

    if (!showFingering.value) {
        props.push(`  :show-fingering="false"`);
    }

    if (!showNoteLabel.value) {
        props.push(`  :show-note-label="false"`);
    }

    if (toleranceCents.value !== 50) {
        props.push(`  :tolerance-cents="${toleranceCents.value}"`);
    }

    if (autoStart.value) {
        props.push(`  :auto-start="true"`);
    }

    if (hideControls.value) {
        props.push(`  :hide-controls="true"`);
    }

    if (noteWidth.value !== 200) {
        props.push(`  :note-width="${noteWidth.value}"`);
    }

    if (noteHeight.value !== 120) {
        props.push(`  :note-height="${noteHeight.value}"`);
    }

    if (fingeringWidth.value !== 500) {
        props.push(`  :fingering-width="${fingeringWidth.value}"`);
    }

    if (fingeringHeight.value !== 180) {
        props.push(`  :fingering-height="${fingeringHeight.value}"`);
    }

    const propsString = props.length > 0 ? "\n" + props.join("\n") + "\n" : "";

    return `<ListeningNote${propsString}/>`;
});

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}
</script>

<style scoped>
.debug-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.checkbox-label {
    display: flex !important;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
}

.text-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
}

.text-input:focus {
    outline: none;
}

.note-hint {
    font-size: 12px;
    font-style: italic;
    opacity: 0.7;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    text-align: center;
    font-weight: 500;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    border-radius: 6px;
    min-height: 500px;
}

.size-controls {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.size-controls label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 14px;
}

.size-control-group {
    margin-bottom: 20px;
}

.size-controls input[type="range"] {
    width: 100%;
    margin-bottom: 0;
}

.preview-info {
    margin-top: 20px;
}

.prop-display div {
    margin-bottom: 5px;
    font-family: monospace;
    font-size: 14px;
    padding: 8px;
    border-radius: 3px;
    font-weight: 500;
}

.instructions-section {
    margin: 40px 0;
}

.usage-list {
    line-height: 1.8;
    padding-left: 20px;
}

.usage-list li {
    margin-bottom: 12px;
}

.usage-list ul {
    margin-top: 8px;
    padding-left: 20px;
}

.status-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    border: 2px solid;
}

.status-badge.status-listening {
    border-color: #6c757d;
    color: #6c757d;
}

.status-badge.status-correct {
    border-color: #28a745;
    color: #28a745;
}

.status-badge.status-close {
    border-color: #ffc107;
    color: #ffc107;
}

.status-badge.status-wrong {
    border-color: #dc3545;
    color: #dc3545;
}

.code-section {
    margin-top: 40px;
}

.code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
    padding: 15px;
    border-radius: 4px;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

.emit-monitor {
    margin-top: 20px;
}

.emit-display {
    font-family: monospace;
    font-size: 14px;
}

.emit-row {
    padding: 8px 12px;
    margin-bottom: 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.02);
    display: flex;
    align-items: center;
    gap: 10px;
}

.emit-row strong {
    min-width: 100px;
    color: #666;
}

.emit-timestamp {
    font-size: 12px;
    opacity: 0.7;
    font-style: italic;
}

.no-emit-message {
    padding: 20px;
    text-align: center;
    font-style: italic;
    opacity: 0.6;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
